import { ReviewAndSendData, ReviewAndSendResponse } from "src/models/AllocationReviewAndSend";
import { AllocationsPayload } from "src/models/Allocation";
import { IsPriorityAllocationFullResponse, MyAllocationsResponse, UpdateAllocationResponse } from "src/models/types";

import { defaultFetch, fetchWithAuth } from "src/utils/auth";
import { refreshAllocationRights } from "./AuthenticationService";

export const myAllocations = async (showId?: number): Promise<MyAllocationsResponse> => {
  try {
    const query = showId != null ? `?showId=${showId}` : "";
    const result = await fetchWithAuth(`/api/backend/v2/allocations/my-allocations${query}`);
    if (result.status >= 400 && result.status < 600) {
      throw new Error("Server not returned a valid response");
    }
    const resultJson = await result.json();
    return new MyAllocationsResponse({ data: resultJson });
  } catch (error) {
    let message;
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = `Unable to load my allocations`;
    }
    return new MyAllocationsResponse({ error: message })
  }
};

export interface BulkUpdateAllocationsResponse {
  data: UpdateAllocationResponse;
  notifiedEmails: string[];
}

export const bulkUpdateAllocations = async (bulkUpdateAllocationCommand: AllocationsPayload): Promise<BulkUpdateAllocationsResponse> => {
  try {
    const body: string = JSON.stringify(bulkUpdateAllocationCommand);
    const res = await fetchWithAuth(`/api/backend/v2/allocations/bulk-update`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body
    });

    const resJson = await res.json();

    if (res.status >= 400) {
      if (
        resJson &&
        resJson.errors &&
        Array.isArray(resJson.errors.message) &&
        resJson.errors.message.length > 0
      ) {
        const errorMessage = resJson.errors.message.join(", ");
        throw new Error(errorMessage);
      }
      throw new Error("Server did not return a valid response");
    }

    // Refresh allocation rights after bulk update
    await refreshAllocationRights();

    return {
      data: new UpdateAllocationResponse({ data: resJson }),
      notifiedEmails: resJson.notifiedEmails ?? [],
    };
  } catch (error) {
    let message
    if (error instanceof Error) {
      message = error.message
    } else {
      message = `Unable to update allocations`
    }
    throw new Error(message)
  }
};

export const myShows = async (): Promise<MyAllocationsResponse> => {
  try {
    const result = await fetchWithAuth(`/api/backend/v2/shows/my-shows`);
    if (result.status >= 400 && result.status < 600) {
      throw new Error("Server not returned a valid response")
    }

    const resultJson = await result.json();

    return new MyAllocationsResponse({ data: resultJson });
  } catch (error) {
    let message
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = `Unable to load my allocations`;
    }

    return new MyAllocationsResponse({ error: message });
  }
};

export const myManagedShows = async (): Promise<MyAllocationsResponse> => {
  try {
    const result = await fetchWithAuth(`/api/backend/v2/shows/my-managed-shows`);
    if (result.status >= 400 && result.status < 600) {
      throw new Error("Server not returned a valid response");
    }

    const resultJson = await result.json();

    return new MyAllocationsResponse({ data: resultJson });
  } catch (error) {
    let message;
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = `Unable to load my managed shows`;
    }

    return new MyAllocationsResponse({ error: message });
  }
};

export const reviewAndSend = async (payload: ReviewAndSendData): Promise<ReviewAndSendResponse> => {
  try {
    const body: string = JSON.stringify(payload);
    const result = await fetchWithAuth(`/api/backend/v2/allocations/bulk-upsert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body
    });

    const resultText = await result.json();
    if (result.status === 401) {
      return new ReviewAndSendResponse({ data: [], error: "You are unauthorized to create or update allocations." });
    }

    if (result.status >= 400 && result.status < 600) {
      return new ReviewAndSendResponse({ data: [], error: resultText });
    }

    // Refresh allocation rights after bulk upsert
    await refreshAllocationRights();

    return new ReviewAndSendResponse({
      data: resultText.allocations ?? resultText,
      notifiedEmails: resultText.notifiedEmails ?? [],
    });
  } catch (error) {
    console.error(error);
    let message = `Unable to create and/or update allocations.`;

    return new ReviewAndSendResponse({ data: [], error: message });
  }
};

export const arePriorityAllocationSessionsFull = async (allocationSessionIdOrMagicLink: string | number, sessionId?: number): Promise<IsPriorityAllocationFullResponse> => {
  try {
    const url = `/api/backend/v2/allocations/${allocationSessionIdOrMagicLink}/are-priority-allocation-sessions-full${sessionId ? "?sessionId=" + sessionId : ""}`;
    const result = await defaultFetch(url);

    if (result.status >= 400 && result.status < 600) {
      throw new Error("Server not returned a valid response");
    }

    const resultJson = await result.json();
    return new IsPriorityAllocationFullResponse({ data: resultJson });
  } catch (error) {
    console.error(error);
    return new IsPriorityAllocationFullResponse({ error: `Unable to determin if session is full.` });
  }
};

export const deleteAllocation = async (allocationId: number): Promise<boolean> => {
  const result = await fetchWithAuth(`/api/backend/v2/allocations/${allocationId}`, {
    method: 'DELETE',
  });

  if (result.status === 200) {
    return true;
  } else if (result.status === 401) {
    throw new Error("You are not authorized to delete this allocation.");
  } else if (result.status === 404) {
    throw new Error("The allocation does not exist.");
  } else {
    const resText = await result.text();
    console.warn(resText);
    throw new Error("Server returned a non-successful response");
  }
};